import {
	PROJECT_LIMITS,
	type ProjectDTO,
	type UpdateProjectBody,
} from "@zplanem/responses"
import type React from "react"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { ApiError } from "../../../lib/api-client"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"

/**
 * The project edit form. Mounts only while editing, so its local state
 * initializes from the current project on mount — no derived-state syncing
 * across an external `editing` flag (and no setState-in-render). The parent
 * unmounts it (via `editing`) on save-success or cancel.
 */
export function ProjectEditForm({
	project: p,
	onSave,
	onDone,
	pending,
}: {
	project: ProjectDTO
	/** `mutateAsync` for the field update; resolves on success, throws on error. */
	onSave: (body: UpdateProjectBody) => Promise<unknown>
	/** Close edit mode (used for both successful save and cancel). */
	onDone: () => void
	pending: boolean
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)

	const [name, setName] = useState(p.name)
	const [description, setDescription] = useState(p.description ?? "")
	const [cost, setCost] = useState(p.estimatedExecutionCost ?? "")
	const [currency, setCurrency] = useState(p.estimatedExecutionCostCurrency)
	const [error, setError] = useState<string | null>(null)

	async function submit(e: React.FormEvent) {
		e.preventDefault()
		const trimmedName = name.trim()
		if (trimmedName.length === 0) {
			setError(t.projects.form.errors.nameRequired)
			return
		}
		const trimmedCost = cost.trim()
		if (trimmedCost.length > 0 && !/^\d{1,10}(\.\d{1,2})?$/.test(trimmedCost)) {
			setError(t.projects.form.errors.costInvalid)
			return
		}
		setError(null)
		const body: UpdateProjectBody = {
			name: trimmedName,
			description: description.trim(),
			estimatedExecutionCost: trimmedCost,
			estimatedExecutionCostCurrency: currency.trim().toUpperCase(),
		}
		try {
			await onSave(body)
			onDone()
		} catch (err) {
			setError(
				err instanceof ApiError ? err.message : t.projects.form.errors.unknown,
			)
		}
	}

	return (
		<form onSubmit={submit} className="mt-4 space-y-4" noValidate>
			<div>
				<label
					htmlFor="edit-name"
					className="block text-sm font-semibold text-foreground/80 mb-2"
				>
					{t.projects.form.nameLabel}
				</label>
				<input
					id="edit-name"
					type="text"
					value={name}
					onChange={(e) => setName(e.target.value)}
					maxLength={PROJECT_LIMITS.nameMax + 10}
					className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-3 text-foreground focus:border-ring outline-none"
				/>
			</div>
			<div>
				<label
					htmlFor="edit-description"
					className="block text-sm font-semibold text-foreground/80 mb-2"
				>
					{t.projects.form.descriptionLabel}
				</label>
				<textarea
					id="edit-description"
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					rows={4}
					maxLength={PROJECT_LIMITS.descriptionMax + 100}
					className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-3 text-foreground focus:border-ring outline-none resize-y"
				/>
			</div>
			<div className="flex gap-3">
				<div className="flex-1">
					<label
						htmlFor="edit-cost"
						className="block text-sm font-semibold text-foreground/80 mb-2"
					>
						{t.projects.detail.estimatedExecutionCostLabel}
					</label>
					<input
						id="edit-cost"
						type="text"
						inputMode="decimal"
						value={cost}
						onChange={(e) => setCost(e.target.value)}
						placeholder={t.projects.detail.costPlaceholder}
						className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring outline-none"
					/>
				</div>
				<div className="w-28">
					<label
						htmlFor="edit-currency"
						className="block text-sm font-semibold text-foreground/80 mb-2"
					>
						{t.projects.detail.currencyLabel}
					</label>
					<input
						id="edit-currency"
						type="text"
						value={currency}
						onChange={(e) => setCurrency(e.target.value)}
						maxLength={3}
						className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-3 text-foreground focus:border-ring outline-none uppercase"
					/>
				</div>
			</div>
			{error ? (
				<div
					role="alert"
					className="rounded-xl border border-destructive/30 bg-destructive/10 text-destructive text-sm px-4 py-3"
				>
					{error}
				</div>
			) : null}
			<div className="flex items-center gap-3">
				<Button type="submit" variant="brand" disabled={pending}>
					{pending ? t.projects.detail.saving : t.projects.detail.save}
				</Button>
				<Button
					type="button"
					variant="ghost"
					onClick={onDone}
					disabled={pending}
				>
					{t.projects.detail.cancel}
				</Button>
			</div>
		</form>
	)
}
